import React, { useState } from 'react';
import { Save, FileText, Send, Calculator, DollarSign, Percent, Calendar, Download } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/Card';
import { Input } from './components/ui/Input';
import { Button } from './components/ui/Button';
import { FormulaRow, Operator } from './components/FormulaRow';
// Utility functions
const parseToFloat = (val: any) => parseFloat(val) || 0;
const diffDays = (date1: string, date2: string) => {
  if (!date1 || !date2) return 0;
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return (d1.getTime() - d2.getTime()) / (1000 * 60 * 60 * 24);
};
const formatCurrency = (val: number) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
}).format(val);
export function App() {
  const [isAdmin] = useState(true); // Default to admin view for demo
  const [showHaircutFields] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  // State
  const [state, setState] = useState({
    borrowerName: 'John Doe',
    disbursementDate: new Date().toISOString().split('T')[0],
    rate: 0.0525,
    optionalHigherHaircut: 0,
    optionalHigherHaircutPercentage: 0,
    // Final CD
    loanAmount: 350000,
    loanAmountMaxPercent: 95,
    originationCharges: 1500,
    appraisalFee: 500,
    creditReport: 50,
    docPrep: 100,
    floodCert: 25,
    taxService: 75,
    fundingFee: 0,
    grmaFee: 0,
    tracPlusFee: 0,
    other: 250,
    prepaidInterestDate: new Date(Date.now() + 15 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    prepaidInterest: 450,
    initialEscrowPayment: 1200,
    lenderCredits: 500,
    // Haircut Comparison
    hairCut: 0,
    daysOnLineEstimated: 15,
    // Purchase Advice
    uwFee: 895,
    purchaseAdviceFloodCert: 25,
    purchaseAdviceTaxService: 75,
    purchaseAdviceGrmaFee: 0,
    purchaseAdviceTracPlusFee: 0,
    toleranceCure: 0,
    escrows: 1200,
    interestFromDate: new Date().toISOString().split('T')[0],
    interestFrom: 300,
    yieldSpread: 1.074,
    // Settlement Statement
    settlementStatementFee: 70,
    settlementStatementInterestThruDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    settlementStatementInterestThru: 600
  });
  const handleChange = (field: string, value: any) => {
    setState(prev => {
      const newState = {
        ...prev,
        [field]: value
      };
      // Bidirectional mapping
      if (field === 'purchaseAdviceFloodCert') newState.floodCert = value;
      if (field === 'floodCert') newState.purchaseAdviceFloodCert = value;
      if (field === 'purchaseAdviceTaxService') newState.taxService = value;
      if (field === 'taxService') newState.purchaseAdviceTaxService = value;
      if (field === 'escrows') newState.initialEscrowPayment = value;
      if (field === 'initialEscrowPayment') newState.escrows = value;
      if (field === 'interestFromDate') newState.settlementStatementInterestThruDate = value;
      if (field === 'settlementStatementInterestThruDate') newState.interestFromDate = value;
      return newState;
    });
  };
  const handleNumChange = (field: string, e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(field, e.target.value === '' ? 0 : parseFloat(e.target.value));
  };
  const handleStrChange = (field: string, e: React.ChangeEvent<HTMLInputElement>) => {
    handleChange(field, e.target.value);
  };
  // --- Calculations ---
  // Final CD Calcs
  const loanAmountMaxAdv = state.loanAmount * (state.loanAmountMaxPercent / 100);
  const totalFundsToSend = state.loanAmount - state.originationCharges - state.appraisalFee - state.creditReport - state.docPrep - state.floodCert - state.taxService - state.fundingFee - state.grmaFee - state.tracPlusFee - state.other - state.prepaidInterest - state.initialEscrowPayment - state.lenderCredits;
  const prepaidInterestDays = diffDays(state.prepaidInterestDate, state.disbursementDate);
  const prepaidInterestPerDiem = prepaidInterestDays ? state.prepaidInterest / prepaidInterestDays : 0;
  const option1 = totalFundsToSend - loanAmountMaxAdv;
  const option2 = state.loanAmount * (state.optionalHigherHaircutPercentage / 100);
  const totalFundsToSendHairCut = Math.max(option1, option2, state.optionalHigherHaircut, 0);
  const haircutToTitle = -totalFundsToSendHairCut;
  const totalFundsSimmons = totalFundsToSend - totalFundsToSendHairCut;
  // Haircut Comparison Calcs
  const defaultHaircut = (totalFundsToSend - Math.max(option1, 0)) * state.rate / 360 * state.daysOnLineEstimated;
  const optionalHaircut = (totalFundsToSend - state.hairCut) * state.rate / 360 * state.daysOnLineEstimated;
  const interestCostSavings = defaultHaircut - optionalHaircut;
  // Purchase Advice Calcs
  const interestFromDays = diffDays(state.prepaidInterestDate, state.interestFromDate);
  const interestFromPerDiem = interestFromDays ? state.interestFrom / interestFromDays : 0;
  const totalSettlementWireReceived = state.loanAmount - state.uwFee - state.purchaseAdviceFloodCert - state.purchaseAdviceTaxService - state.purchaseAdviceGrmaFee - state.purchaseAdviceTracPlusFee - state.toleranceCure - state.escrows - state.interestFrom + state.yieldSpread;
  // Settlement Statement Calcs
  const days360Basis = diffDays(state.settlementStatementInterestThruDate, state.disbursementDate);
  const settlementStatementInterestThruPerDiem = days360Basis ? state.settlementStatementInterestThru / days360Basis : 0;
  const settlementStatementResidual = totalSettlementWireReceived - totalFundsSimmons - state.settlementStatementFee - state.settlementStatementInterestThru;
  const totalNetIncome = settlementStatementResidual - totalFundsToSendHairCut;
  // Income Breakdown Calcs
  const WHSpread = state.prepaidInterest - state.interestFrom - state.settlementStatementInterestThru;
  const incomeBreakdownNetIncome = state.originationCharges + state.lenderCredits + state.appraisalFee + state.creditReport + state.docPrep + state.floodCert + state.taxService + state.fundingFee + state.grmaFee + state.tracPlusFee + state.initialEscrowPayment + state.other - state.uwFee - state.purchaseAdviceFloodCert - state.purchaseAdviceTaxService - state.purchaseAdviceGrmaFee - state.purchaseAdviceTracPlusFee - state.escrows - state.toleranceCure + state.yieldSpread - state.settlementStatementFee + WHSpread;
  // Cashflow Calcs
  const cashFlowNetIncome = settlementStatementResidual + haircutToTitle;
  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => setIsSaving(false), 1000);
  };
  return <div className="min-h-screen bg-slate-50 p-4 md:p-8 font-sans text-slate-900" data-id="element-0">
      <div className="max-w-6xl mx-auto space-y-6" data-id="element-1">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white/95 backdrop-blur-sm p-5 rounded-xl shadow-sm border border-slate-200 sticky top-4 z-10" data-id="element-2">
          <div data-id="element-3">
            <h1 className="text-2xl font-bold flex items-center gap-2" data-id="element-4">
              <Calculator className="w-6 h-6 text-blue-600" data-id="element-5" />
              Reconciliation Calculator
            </h1>
            <p className="text-sm text-slate-500 mt-1" data-id="element-6">
              Review and calculate loan settlement figures.
            </p>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto flex-wrap" data-id="element-7">
            <Button variant="ghost" leftIcon={<Download className="w-4 h-4" data-id="element-9" />} onClick={() => window.print()} data-id="element-8">
              Export PDF
            </Button>
            {!isAdmin && <Button variant="destructive" leftIcon={<Send className="w-4 h-4" data-id="element-11" />} data-id="element-10">
                Request Wire Order
              </Button>}
            <Button variant="outline" leftIcon={<Save className="w-4 h-4" data-id="element-13" />} onClick={handleSave} isLoading={isSaving} data-id="element-12">
              Save Calculation
            </Button>
            {isAdmin && <Button variant="primary" leftIcon={<FileText className="w-4 h-4" data-id="element-15" />} onClick={handleSave} isLoading={isSaving} data-id="element-14">
                Save and Reconcile
              </Button>}
          </div>
        </div>

        {/* Borrower Info */}
        <Card data-id="element-16">
          <CardHeader data-id="element-17">
            <CardTitle data-id="element-18">Borrower Information</CardTitle>
          </CardHeader>
          <CardContent data-id="element-19">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-20">
              <Input label="Borrower Name" value={state.borrowerName} onChange={e => handleStrChange('borrowerName', e)} variant="green" data-id="element-21" />
              <Input label="Disbursement Date" type="date" value={state.disbursementDate} onChange={e => handleStrChange('disbursementDate', e)} variant="green" leftIcon={<Calendar className="w-4 h-4" data-id="element-23" />} data-id="element-22" />
              <Input label="Rate" type="number" step="0.0001" value={state.rate} onChange={e => handleNumChange('rate', e)} variant="green" leftIcon={<Percent className="w-4 h-4" data-id="element-25" />} data-id="element-24" />
            </div>

            {showHaircutFields && <div className="mt-6 pt-6 border-t border-slate-100" data-id="element-26">
                <label className="text-sm font-medium text-slate-700 mb-3 block" data-id="element-27">
                  Optional Higher Haircut
                </label>
                <div className="flex items-center gap-4" data-id="element-28">
                  <Input type="number" value={state.optionalHigherHaircut} onChange={e => handleNumChange('optionalHigherHaircut', e)} variant="orange" leftIcon={<DollarSign className="w-4 h-4" data-id="element-30" />} containerClassName="w-48" data-id="element-29" />
                  <span className="text-slate-400 font-medium italic" data-id="element-31">or</span>
                  <Input type="number" value={state.optionalHigherHaircutPercentage} onChange={e => handleNumChange('optionalHigherHaircutPercentage', e)} variant="orange" leftIcon={<Percent className="w-4 h-4" data-id="element-33" />} containerClassName="w-48" data-id="element-32" />
                </div>
              </div>}
          </CardContent>
        </Card>

        {/* Final CD */}
        <Card data-id="element-34">
          <CardHeader className="bg-slate-800 text-white rounded-t-xl" data-id="element-35">
            <CardTitle className="text-white" data-id="element-36">Final CD</CardTitle>
            <p className="text-sm text-slate-300 mt-1" data-id="element-37">
              Use Final CD at time of funding
            </p>
          </CardHeader>
          <CardContent className="space-y-1" data-id="element-38">
            <FormulaRow label="Loan Terms" subLabel="Loan Amount" data-id="element-39">
              <div className="flex flex-col gap-1 shrink-0" data-id="element-40">
                <span className="text-xs text-slate-500 font-medium" data-id="element-41">
                  &nbsp;
                </span>
                <Input type="number" value={state.loanAmount} onChange={e => handleNumChange('loanAmount', e)} variant="green" leftIcon={<DollarSign className="w-4 h-4" data-id="element-43" />} className="w-36" data-id="element-42" />
              </div>
              <Operator symbol="×" data-id="element-44" />
              <div className="flex flex-col gap-1 shrink-0" data-id="element-45">
                <span className="text-xs text-slate-500 font-medium" data-id="element-46">
                  Max %
                </span>
                <Input type="number" value={state.loanAmountMaxPercent} onChange={e => handleNumChange('loanAmountMaxPercent', e)} variant="orange" rightIcon={<Percent className="w-4 h-4" data-id="element-48" />} className="w-24" data-id="element-47" />
              </div>
              <Operator symbol="=" data-id="element-49" />
              <div className="flex flex-col gap-1 shrink-0" data-id="element-50">
                <span className="text-xs text-slate-500 font-medium" data-id="element-51">
                  Max Adv
                </span>
                <Input type="number" value={loanAmountMaxAdv.toFixed(2)} disabled leftIcon={<DollarSign className="w-4 h-4" data-id="element-53" />} className="w-36" data-id="element-52" />
              </div>
            </FormulaRow>

            <FormulaRow label="A. Origination Charges" subLabel="Total for Section" data-id="element-54">
              <Operator symbol="-" data-id="element-55" />
              <Input type="number" value={state.originationCharges} onChange={e => handleNumChange('originationCharges', e)} variant="green" leftIcon={<DollarSign className="w-4 h-4" data-id="element-57" />} className="w-32" data-id="element-56" />
            </FormulaRow>

            <div className="py-3 mt-1" data-id="element-58">
              <h4 className="text-sm font-semibold text-slate-800 border-l-3 border-slate-300 pl-3" data-id="element-59">
                B. Services Borrower Did Not Shop For
              </h4>
            </div>

            <FormulaRow label="Appraisal Fee" data-id="element-60">
              <Operator symbol="-" data-id="element-61" />
              <Input type="number" value={state.appraisalFee} onChange={e => handleNumChange('appraisalFee', e)} variant="green" leftIcon={<DollarSign className="w-4 h-4" data-id="element-63" />} className="w-32" data-id="element-62" />
            </FormulaRow>

            <FormulaRow label="Credit Report" data-id="element-64">
              <Operator symbol="-" data-id="element-65" />
              <Input type="number" value={state.creditReport} onChange={e => handleNumChange('creditReport', e)} variant="green" leftIcon={<DollarSign className="w-4 h-4" data-id="element-67" />} className="w-32" data-id="element-66" />
            </FormulaRow>

            <FormulaRow label="MERS" data-id="element-68">
              <Operator symbol="-" data-id="element-69" />
              <Input type="number" value={state.docPrep} onChange={e => handleNumChange('docPrep', e)} variant="green" leftIcon={<DollarSign className="w-4 h-4" data-id="element-71" />} className="w-32" data-id="element-70" />
            </FormulaRow>

            <FormulaRow label="Flood Cert" data-id="element-72">
              <Operator symbol="-" data-id="element-73" />
              <Input type="number" value={state.floodCert} onChange={e => handleNumChange('floodCert', e)} variant="green" leftIcon={<DollarSign className="w-4 h-4" data-id="element-75" />} className="w-32" data-id="element-74" />
            </FormulaRow>

            <FormulaRow label="Tax Service" data-id="element-76">
              <Operator symbol="-" data-id="element-77" />
              <Input type="number" value={state.taxService} onChange={e => handleNumChange('taxService', e)} variant="green" leftIcon={<DollarSign className="w-4 h-4" data-id="element-79" />} className="w-32" data-id="element-78" />
            </FormulaRow>

            <FormulaRow label="FHA or VA Funding Fee" data-id="element-80">
              <Operator symbol="-" data-id="element-81" />
              <Input type="number" value={state.fundingFee} onChange={e => handleNumChange('fundingFee', e)} variant="green" leftIcon={<DollarSign className="w-4 h-4" data-id="element-83" />} className="w-32" data-id="element-82" />
            </FormulaRow>

            <FormulaRow label="GRMA Fee (Georgia)" data-id="element-84">
              <Operator symbol="-" data-id="element-85" />
              <Input type="number" value={state.grmaFee} onChange={e => handleNumChange('grmaFee', e)} variant="green" leftIcon={<DollarSign className="w-4 h-4" data-id="element-87" />} className="w-32" data-id="element-86" />
            </FormulaRow>

            <FormulaRow label="Trac+ Fee (UWM)" data-id="element-88">
              <Operator symbol="-" data-id="element-89" />
              <Input type="number" value={state.tracPlusFee} onChange={e => handleNumChange('tracPlusFee', e)} variant="green" leftIcon={<DollarSign className="w-4 h-4" data-id="element-91" />} className="w-32" data-id="element-90" />
            </FormulaRow>

            <FormulaRow label="Other" data-id="element-92">
              <Operator symbol="-" data-id="element-93" />
              <Input type="number" value={state.other} onChange={e => handleNumChange('other', e)} variant="green" leftIcon={<DollarSign className="w-4 h-4" data-id="element-95" />} className="w-32" data-id="element-94" />
            </FormulaRow>

            <FormulaRow label="F. Prepaids" subLabel="Prepaid Interest (thru)" data-id="element-96">
              <Input type="date" value={state.prepaidInterestDate} onChange={e => handleStrChange('prepaidInterestDate', e)} variant="green" className="w-40" data-id="element-97" />
              <Operator symbol="-" data-id="element-98" />
              <Input type="number" value={state.prepaidInterest} onChange={e => handleNumChange('prepaidInterest', e)} variant="green" leftIcon={<DollarSign className="w-4 h-4" data-id="element-100" />} className="w-32" data-id="element-99" />
              <Operator symbol="=" data-id="element-101" />
              <div className="flex flex-col gap-1" data-id="element-102">
                <span className="text-xs text-slate-500 font-medium" data-id="element-103">Days</span>
                <Input type="number" value={prepaidInterestDays.toFixed(0)} disabled className="w-20" data-id="element-104" />
              </div>
              <Operator symbol="×" data-id="element-105" />
              <div className="flex flex-col gap-1" data-id="element-106">
                <span className="text-xs text-slate-500 font-medium" data-id="element-107">
                  Per Diem
                </span>
                <Input type="number" value={prepaidInterestPerDiem.toFixed(4)} disabled leftIcon={<DollarSign className="w-4 h-4" data-id="element-109" />} className="w-32" data-id="element-108" />
              </div>
            </FormulaRow>

            <FormulaRow label="G. Initial Escrow Payment" subLabel="Total for Section" data-id="element-110">
              <Operator symbol="-" data-id="element-111" />
              <Input type="number" value={state.initialEscrowPayment} onChange={e => handleNumChange('initialEscrowPayment', e)} variant="green" leftIcon={<DollarSign className="w-4 h-4" data-id="element-113" />} className="w-32" data-id="element-112" />
            </FormulaRow>

            <FormulaRow label="J. Total Closing Costs" subLabel="Lender Credits" data-id="element-114">
              <Operator symbol="-" data-id="element-115" />
              <Input type="number" value={state.lenderCredits} onChange={e => handleNumChange('lenderCredits', e)} variant="green" leftIcon={<DollarSign className="w-4 h-4" data-id="element-117" />} className="w-32" data-id="element-116" />
            </FormulaRow>

            <div className="mt-6 bg-blue-50 rounded-xl p-6 border border-blue-100" data-id="element-118">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6" data-id="element-119">
                <div data-id="element-120">
                  <h3 className="text-lg font-bold text-blue-900" data-id="element-121">
                    Wire Amount
                  </h3>
                  <p className="text-sm text-blue-700" data-id="element-122">Total Funds to Send</p>
                </div>
                <div className="flex items-center gap-4 flex-wrap" data-id="element-123">
                  <div className="flex flex-col" data-id="element-124">
                    <span className="text-xs font-semibold text-blue-800 mb-1" data-id="element-125">
                      Total
                    </span>
                    <div className="text-2xl font-bold text-blue-900 bg-white px-4 py-2 rounded-lg border border-blue-200 shadow-sm" data-id="element-126">
                      {formatCurrency(totalFundsToSend)}
                    </div>
                  </div>
                  <Operator symbol="=" data-id="element-127" />
                  <div className="flex flex-col" data-id="element-128">
                    <span className="text-xs font-semibold text-blue-800 mb-1" data-id="element-129">
                      Haircut
                    </span>
                    <Input type="number" value={totalFundsToSendHairCut.toFixed(2)} disabled leftIcon={<DollarSign className="w-4 h-4" data-id="element-131" />} className="w-32 border-blue-200" data-id="element-130" />
                  </div>
                  <Operator symbol="+" data-id="element-132" />
                  <div className="flex flex-col" data-id="element-133">
                    <span className="text-xs font-semibold text-blue-800 mb-1" data-id="element-134">
                      Simmons
                    </span>
                    <Input type="number" value={totalFundsSimmons.toFixed(2)} disabled leftIcon={<DollarSign className="w-4 h-4" data-id="element-136" />} className="w-32 border-blue-200" data-id="element-135" />
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {isAdmin && showHaircutFields && <Card data-id="element-137">
            <CardHeader className="bg-slate-800 text-white rounded-t-xl" data-id="element-138">
              <CardTitle className="text-white" data-id="element-139">
                Optional Haircut Comparison
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-1" data-id="element-140">
              <FormulaRow label="Haircut" data-id="element-141">
                <Input type="number" value={state.hairCut} onChange={e => handleNumChange('hairCut', e)} variant="orange" leftIcon={<DollarSign className="w-4 h-4" data-id="element-143" />} className="w-32" data-id="element-142" />
              </FormulaRow>
              <FormulaRow label="Days on Line Estimated" data-id="element-144">
                <Input type="number" value={state.daysOnLineEstimated} onChange={e => handleNumChange('daysOnLineEstimated', e)} variant="orange" className="w-32" data-id="element-145" />
              </FormulaRow>
              <FormulaRow label="Interest Cost" subLabel="(Default Haircut vs Optional Haircut)" data-id="element-146">
                <Input type="number" value={defaultHaircut.toFixed(2)} disabled leftIcon={<DollarSign className="w-4 h-4" data-id="element-148" />} className="w-32" data-id="element-147" />
                <Operator symbol="-" data-id="element-149" />
                <Input type="number" value={optionalHaircut.toFixed(2)} disabled leftIcon={<DollarSign className="w-4 h-4" data-id="element-151" />} className="w-32" data-id="element-150" />
                <Operator symbol="=" data-id="element-152" />
                <div className="flex flex-col gap-1" data-id="element-153">
                  <span className="text-xs text-slate-500 font-medium" data-id="element-154">
                    Savings
                  </span>
                  <Input type="number" value={interestCostSavings.toFixed(2)} disabled leftIcon={<DollarSign className="w-4 h-4" data-id="element-156" />} className="w-32" data-id="element-155" />
                </div>
              </FormulaRow>
            </CardContent>
          </Card>}

        {isAdmin && <>
            <Card data-id="element-157">
              <CardHeader className="bg-slate-800 text-white rounded-t-xl" data-id="element-158">
                <CardTitle className="text-white" data-id="element-159">Purchase Advice</CardTitle>
                <p className="text-sm text-slate-300 mt-1" data-id="element-160">
                  Use Purchase Advice at time loan pays off your line
                </p>
              </CardHeader>
              <CardContent className="space-y-1" data-id="element-161">
                <FormulaRow label="Loan Amount" data-id="element-162">
                  <Operator symbol="+" data-id="element-163" />
                  <Input type="number" value={state.loanAmount} disabled leftIcon={<DollarSign className="w-4 h-4" data-id="element-165" />} className="w-32" data-id="element-164" />
                </FormulaRow>
                <FormulaRow label="UW Fee" data-id="element-166">
                  <Operator symbol="-" data-id="element-167" />
                  <Input type="number" value={state.uwFee} onChange={e => handleNumChange('uwFee', e)} variant="green" leftIcon={<DollarSign className="w-4 h-4" data-id="element-169" />} className="w-32" data-id="element-168" />
                </FormulaRow>
                <FormulaRow label="Flood Cert" data-id="element-170">
                  <Operator symbol="-" data-id="element-171" />
                  <Input type="number" value={state.purchaseAdviceFloodCert} onChange={e => handleNumChange('purchaseAdviceFloodCert', e)} variant="green" leftIcon={<DollarSign className="w-4 h-4" data-id="element-173" />} className="w-32" data-id="element-172" />
                </FormulaRow>
                <FormulaRow label="Tax Service" data-id="element-174">
                  <Operator symbol="-" data-id="element-175" />
                  <Input type="number" value={state.purchaseAdviceTaxService} onChange={e => handleNumChange('purchaseAdviceTaxService', e)} variant="green" leftIcon={<DollarSign className="w-4 h-4" data-id="element-177" />} className="w-32" data-id="element-176" />
                </FormulaRow>
                <FormulaRow label="Tolerance Cure / Other" data-id="element-178">
                  <Operator symbol="-" data-id="element-179" />
                  <Input type="number" value={state.toleranceCure} onChange={e => handleNumChange('toleranceCure', e)} variant="green" leftIcon={<DollarSign className="w-4 h-4" data-id="element-181" />} className="w-32" data-id="element-180" />
                </FormulaRow>
                <FormulaRow label="Escrows" data-id="element-182">
                  <Operator symbol="-" data-id="element-183" />
                  <Input type="number" value={state.escrows} onChange={e => handleNumChange('escrows', e)} variant="green" leftIcon={<DollarSign className="w-4 h-4" data-id="element-185" />} className="w-32" data-id="element-184" />
                </FormulaRow>
                <FormulaRow label="GRMA Fee (Georgia)" data-id="element-186">
                  <Operator symbol="-" data-id="element-187" />
                  <Input type="number" value={state.purchaseAdviceGrmaFee} onChange={e => handleNumChange('purchaseAdviceGrmaFee', e)} variant="green" leftIcon={<DollarSign className="w-4 h-4" data-id="element-189" />} className="w-32" data-id="element-188" />
                </FormulaRow>
                <FormulaRow label="Trac+ Fee (UWM)" data-id="element-190">
                  <Operator symbol="-" data-id="element-191" />
                  <Input type="number" value={state.purchaseAdviceTracPlusFee} onChange={e => handleNumChange('purchaseAdviceTracPlusFee', e)} variant="green" leftIcon={<DollarSign className="w-4 h-4" data-id="element-193" />} className="w-32" data-id="element-192" />
                </FormulaRow>
                <FormulaRow label="Interest (from)" data-id="element-194">
                  <Input type="date" value={state.interestFromDate} onChange={e => handleStrChange('interestFromDate', e)} variant="green" className="w-40" data-id="element-195" />
                  <Operator symbol="-" data-id="element-196" />
                  <Input type="number" value={state.interestFrom} onChange={e => handleNumChange('interestFrom', e)} variant="green" leftIcon={<DollarSign className="w-4 h-4" data-id="element-198" />} className="w-32" data-id="element-197" />
                  <Operator symbol="=" data-id="element-199" />
                  <div className="flex flex-col gap-1" data-id="element-200">
                    <span className="text-xs text-slate-500 font-medium" data-id="element-201">
                      Days
                    </span>
                    <Input type="number" value={interestFromDays.toFixed(0)} disabled className="w-20" data-id="element-202" />
                  </div>
                  <Operator symbol="×" data-id="element-203" />
                  <div className="flex flex-col gap-1" data-id="element-204">
                    <span className="text-xs text-slate-500 font-medium" data-id="element-205">
                      Per Diem
                    </span>
                    <Input type="number" value={interestFromPerDiem.toFixed(4)} disabled leftIcon={<DollarSign className="w-4 h-4" data-id="element-207" />} className="w-32" data-id="element-206" />
                  </div>
                </FormulaRow>
                <FormulaRow label="Yield Spread" data-id="element-208">
                  <Operator symbol="+" data-id="element-209" />
                  <Input type="number" value={state.yieldSpread} onChange={e => handleNumChange('yieldSpread', e)} variant="green" leftIcon={<DollarSign className="w-4 h-4" data-id="element-211" />} className="w-32" data-id="element-210" />
                  <Operator symbol="=" data-id="element-212" />
                  <div className="flex flex-col gap-1" data-id="element-213">
                    <span className="text-xs text-slate-500 font-medium" data-id="element-214">
                      Premium
                    </span>
                    <Input type="text" value={`${state.loanAmount ? (state.yieldSpread / state.loanAmount * 100).toFixed(3) : '0.000'}%`} disabled className="w-24" data-id="element-215" />
                  </div>
                </FormulaRow>

                <div className="mt-6 bg-emerald-50 rounded-xl p-6 border border-emerald-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-216">
                  <div data-id="element-217">
                    <h3 className="text-lg font-bold text-emerald-900" data-id="element-218">
                      Investor Wired
                    </h3>
                    <p className="text-sm text-emerald-700" data-id="element-219">
                      Total Settlement Wire Received
                    </p>
                  </div>
                  <div className="text-2xl font-bold text-emerald-900 bg-white px-6 py-3 rounded-xl border border-emerald-200 shadow-sm" data-id="element-220">
                    {formatCurrency(totalSettlementWireReceived)}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card data-id="element-221">
              <CardHeader className="bg-slate-800 text-white rounded-t-xl" data-id="element-222">
                <CardTitle className="text-white" data-id="element-223">
                  Settlement Statement
                </CardTitle>
                <p className="text-sm text-slate-300 mt-1" data-id="element-224">
                  Use Settlement Statement from WLS at time loan pays off your
                  line
                </p>
              </CardHeader>
              <CardContent className="space-y-1" data-id="element-225">
                <FormulaRow label="Investor Wire" data-id="element-226">
                  <Operator symbol="+" data-id="element-227" />
                  <Input type="number" value={totalSettlementWireReceived.toFixed(2)} disabled leftIcon={<DollarSign className="w-4 h-4" data-id="element-229" />} className="w-32" data-id="element-228" />
                </FormulaRow>
                <FormulaRow label="Principal" data-id="element-230">
                  <Operator symbol="-" data-id="element-231" />
                  <Input type="number" value={totalFundsSimmons.toFixed(2)} disabled leftIcon={<DollarSign className="w-4 h-4" data-id="element-233" />} className="w-32" data-id="element-232" />
                </FormulaRow>
                <FormulaRow label="Fee" data-id="element-234">
                  <Operator symbol="-" data-id="element-235" />
                  <Input type="number" value={state.settlementStatementFee} onChange={e => handleNumChange('settlementStatementFee', e)} variant="green" leftIcon={<DollarSign className="w-4 h-4" data-id="element-237" />} className="w-32" data-id="element-236" />
                </FormulaRow>
                <FormulaRow label="Interest (thru)" data-id="element-238">
                  <Input type="date" value={state.settlementStatementInterestThruDate} onChange={e => handleStrChange('settlementStatementInterestThruDate', e)} variant="green" className="w-40" data-id="element-239" />
                  <Operator symbol="-" data-id="element-240" />
                  <Input type="number" value={state.settlementStatementInterestThru} onChange={e => handleNumChange('settlementStatementInterestThru', e)} variant="green" leftIcon={<DollarSign className="w-4 h-4" data-id="element-242" />} className="w-32" data-id="element-241" />
                  <Operator symbol="=" data-id="element-243" />
                  <div className="flex flex-col gap-1" data-id="element-244">
                    <span className="text-xs text-slate-500 font-medium" data-id="element-245">
                      Days 360 Basis
                    </span>
                    <Input type="number" value={days360Basis.toFixed(0)} disabled className="w-24" data-id="element-246" />
                  </div>
                  <Operator symbol="×" data-id="element-247" />
                  <div className="flex flex-col gap-1" data-id="element-248">
                    <span className="text-xs text-slate-500 font-medium" data-id="element-249">
                      Per Diem
                    </span>
                    <Input type="number" value={settlementStatementInterestThruPerDiem.toFixed(4)} disabled leftIcon={<DollarSign className="w-4 h-4" data-id="element-251" />} className="w-32" data-id="element-250" />
                  </div>
                </FormulaRow>
                <FormulaRow label="Residual" data-id="element-252">
                  <Operator symbol="=" data-id="element-253" />
                  <Input type="number" value={settlementStatementResidual.toFixed(2)} disabled leftIcon={<DollarSign className="w-4 h-4" data-id="element-255" />} className="w-32" data-id="element-254" />
                </FormulaRow>
                <FormulaRow label="Haircut" data-id="element-256">
                  <Operator symbol="-" data-id="element-257" />
                  <Input type="number" value={totalFundsToSendHairCut.toFixed(2)} disabled leftIcon={<DollarSign className="w-4 h-4" data-id="element-259" />} className="w-32" data-id="element-258" />
                </FormulaRow>

                <div className="mt-6 bg-indigo-50 rounded-xl p-6 border border-indigo-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-260">
                  <h3 className="text-xl font-bold text-indigo-900" data-id="element-261">
                    Total Net Income
                  </h3>
                  <div className="text-2xl font-bold text-indigo-900 bg-white px-6 py-3 rounded-xl border border-indigo-200 shadow-sm" data-id="element-262">
                    {formatCurrency(totalNetIncome)}
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-263">
              <Card data-id="element-264">
                <CardHeader className="bg-slate-800 text-white rounded-t-xl" data-id="element-265">
                  <CardTitle className="text-white" data-id="element-266">Income Breakdown</CardTitle>
                </CardHeader>
                <CardContent data-id="element-267">
                  <div className="space-y-6" data-id="element-268">
                    <div data-id="element-269">
                      <h4 className="text-sm font-bold text-slate-800 mb-3 uppercase tracking-wider" data-id="element-270">
                        From CD
                      </h4>
                      <div className="space-y-2" data-id="element-271">
                        <div className="flex justify-between text-sm" data-id="element-272">
                          <span className="text-slate-600" data-id="element-273">Orig Fee</span>
                          <span className="font-medium" data-id="element-274">
                            {formatCurrency(state.originationCharges)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm" data-id="element-275">
                          <span className="text-slate-600" data-id="element-276">Lender Credit</span>
                          <span className="font-medium" data-id="element-277">
                            {formatCurrency(state.lenderCredits)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm" data-id="element-278">
                          <span className="text-slate-600" data-id="element-279">Appraisal</span>
                          <span className="font-medium" data-id="element-280">
                            {formatCurrency(state.appraisalFee)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm" data-id="element-281">
                          <span className="text-slate-600" data-id="element-282">Credit Report</span>
                          <span className="font-medium" data-id="element-283">
                            {formatCurrency(state.creditReport)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm" data-id="element-284">
                          <span className="text-slate-600" data-id="element-285">MERS</span>
                          <span className="font-medium" data-id="element-286">
                            {formatCurrency(state.docPrep)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm" data-id="element-287">
                          <span className="text-slate-600" data-id="element-288">Flood Cert</span>
                          <span className="font-medium" data-id="element-289">
                            {formatCurrency(state.floodCert)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm" data-id="element-290">
                          <span className="text-slate-600" data-id="element-291">Tax Service</span>
                          <span className="font-medium" data-id="element-292">
                            {formatCurrency(state.taxService)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm" data-id="element-293">
                          <span className="text-slate-600" data-id="element-294">
                            FHA/VA Funding Fee
                          </span>
                          <span className="font-medium" data-id="element-295">
                            {formatCurrency(state.fundingFee)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm" data-id="element-296">
                          <span className="text-slate-600" data-id="element-297">Escrows</span>
                          <span className="font-medium" data-id="element-298">
                            {formatCurrency(state.initialEscrowPayment)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm" data-id="element-299">
                          <span className="text-slate-600" data-id="element-300">
                            GRMA Fee (Georgia)
                          </span>
                          <span className="font-medium" data-id="element-301">
                            {formatCurrency(state.grmaFee)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm" data-id="element-302">
                          <span className="text-slate-600" data-id="element-303">
                            Trac+ Fee (UWM)
                          </span>
                          <span className="font-medium" data-id="element-304">
                            {formatCurrency(state.tracPlusFee)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm" data-id="element-305">
                          <span className="text-slate-600" data-id="element-306">Other</span>
                          <span className="font-medium" data-id="element-307">
                            {formatCurrency(state.other)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-slate-100" data-id="element-308">
                      <h4 className="text-sm font-bold text-slate-800 mb-3 uppercase tracking-wider" data-id="element-309">
                        Investor
                      </h4>
                      <div className="space-y-2" data-id="element-310">
                        <div className="flex justify-between text-sm" data-id="element-311">
                          <span className="text-slate-600" data-id="element-312">UW Fee</span>
                          <span className="font-medium text-red-600" data-id="element-313">
                            {formatCurrency(-state.uwFee)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm" data-id="element-314">
                          <span className="text-slate-600" data-id="element-315">Flood Cert</span>
                          <span className="font-medium text-red-600" data-id="element-316">
                            {formatCurrency(-state.purchaseAdviceFloodCert)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm" data-id="element-317">
                          <span className="text-slate-600" data-id="element-318">Tax Service</span>
                          <span className="font-medium text-red-600" data-id="element-319">
                            {formatCurrency(-state.purchaseAdviceTaxService)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm" data-id="element-320">
                          <span className="text-slate-600" data-id="element-321">Escrows</span>
                          <span className="font-medium text-red-600" data-id="element-322">
                            {formatCurrency(-state.escrows)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm" data-id="element-323">
                          <span className="text-slate-600" data-id="element-324">
                            GRMA Fee (Georgia)
                          </span>
                          <span className="font-medium text-red-600" data-id="element-325">
                            {formatCurrency(-state.purchaseAdviceGrmaFee)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm" data-id="element-326">
                          <span className="text-slate-600" data-id="element-327">
                            Trac+ Fee (UWM)
                          </span>
                          <span className="font-medium text-red-600" data-id="element-328">
                            {formatCurrency(-state.purchaseAdviceTracPlusFee)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm" data-id="element-329">
                          <span className="text-slate-600" data-id="element-330">Tolerance Cure</span>
                          <span className="font-medium text-red-600" data-id="element-331">
                            {formatCurrency(-state.toleranceCure)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm" data-id="element-332">
                          <span className="text-slate-600" data-id="element-333">Yield Spread</span>
                          <span className="font-medium" data-id="element-334">
                            {formatCurrency(state.yieldSpread)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-slate-100" data-id="element-335">
                      <h4 className="text-sm font-bold text-slate-800 mb-3 uppercase tracking-wider" data-id="element-336">
                        Simmons
                      </h4>
                      <div className="space-y-2" data-id="element-337">
                        <div className="flex justify-between text-sm" data-id="element-338">
                          <span className="text-slate-600" data-id="element-339">WH Fee</span>
                          <span className="font-medium text-red-600" data-id="element-340">
                            {formatCurrency(-state.settlementStatementFee)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm" data-id="element-341">
                          <span className="text-slate-600" data-id="element-342">WH Spread</span>
                          <span className="font-medium" data-id="element-343">
                            {formatCurrency(WHSpread)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t-2 border-slate-800 flex justify-between items-center" data-id="element-344">
                      <span className="text-lg font-bold text-slate-900" data-id="element-345">
                        Net Income
                      </span>
                      <span className="text-xl font-bold text-indigo-600" data-id="element-346">
                        {formatCurrency(incomeBreakdownNetIncome)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card data-id="element-347">
                <CardHeader className="bg-slate-800 text-white rounded-t-xl" data-id="element-348">
                  <CardTitle className="text-white" data-id="element-349">
                    Cashflow Breakdown
                  </CardTitle>
                </CardHeader>
                <CardContent data-id="element-350">
                  <div className="space-y-4" data-id="element-351">
                    <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg" data-id="element-352">
                      <span className="text-slate-700 font-medium" data-id="element-353">
                        Paid from Simmons
                      </span>
                      <span className="font-semibold text-slate-900" data-id="element-354">
                        {formatCurrency(settlementStatementResidual)}
                      </span>
                    </div>
                    <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg" data-id="element-355">
                      <span className="text-slate-700 font-medium" data-id="element-356">
                        Haircut To Title
                      </span>
                      <span className="font-semibold text-red-600" data-id="element-357">
                        {formatCurrency(haircutToTitle)}
                      </span>
                    </div>
                    <div className="pt-4 mt-4 border-t-2 border-slate-800 flex justify-between items-center" data-id="element-358">
                      <span className="text-lg font-bold text-slate-900" data-id="element-359">
                        Net Income
                      </span>
                      <span className="text-xl font-bold text-indigo-600" data-id="element-360">
                        {formatCurrency(cashFlowNetIncome)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </>}
      </div>
    </div>;
}